import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	Modal,
	Paper,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Tooltip,
	Typography,
} from '@material-ui/core'
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import { CAMPAIGN_WITHOUT_PRODUCT_SELECTION } from '../../../constants/marketingCampaign'
import { INPUT_TYPE } from '../../../constants/input'
import {
	CampaignModalSkeletonProps,
	CampaignConfirmationState,
	CampaignImageConfig,
} from './CampaignModalSkeleton.types'
import {
	useCampaignModalSkeletonStyles,
	CAMPAIGN_ICON_TEXT_GAP_PX,
} from './CampaignModalSkeleton.styles'
import {
	createDefaultConfirmation,
	confirmationFromCampaign,
	hasRequiredCampaignNaming,
} from './CampaignModalSkeleton.helpers'
import {
	CAMPAIGN_IMAGE_ACCEPTED_EXTENSIONS,
	CAMPAIGN_IMAGE_ACCEPTED_MIME_TYPES,
	CAMPAIGN_IMAGE_BAD_TYPE_MSG,
	CAMPAIGN_IMAGE_MAX_BYTES,
	CAMPAIGN_IMAGE_REQUIRED_MSG,
	CAMPAIGN_IMAGE_TOO_BIG_MSG,
	CAMPAIGN_NAME_REQUIRED_MSG,
} from './CampaignModalSkeleton.imageUrl'
import { RestaurantsStep } from './steps/RestaurantsStep.view'
import { ConfirmationStep } from './steps/ConfirmationStep.view'
import { CampaignType } from '../../../constants/marketingCampaign'
import { BNGM_SCOPE } from '../campaigns/buyNGetMCheapestAtDiscount/types'

function NamingImageControl({
	image,
	isMissing = false,
	onSelectImage,
	onRemoveImage,
	onError,
	classes,
}: {
	image: string | null
	isMissing?: boolean
	onSelectImage: (dataUrl: string) => void
	onRemoveImage: () => void
	onError: (message: string) => void
	classes: ReturnType<typeof useCampaignModalSkeletonStyles>
}) {
	const inputRef = React.useRef<HTMLInputElement | null>(null)
	const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null)

	const openPicker = React.useCallback(() => {
		inputRef.current?.click()
	}, [])

	const handleFileChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			e.target.value = ''
			if (!file) {
				return
			}
			if (!CAMPAIGN_IMAGE_ACCEPTED_MIME_TYPES.includes(file.type)) {
				onError(CAMPAIGN_IMAGE_BAD_TYPE_MSG)
				return
			}
			if (file.size > CAMPAIGN_IMAGE_MAX_BYTES) {
				onError(CAMPAIGN_IMAGE_TOO_BIG_MSG)
				return
			}
			const reader = new FileReader()
			reader.onload = () => {
				const result = reader.result
				if (typeof result === 'string') {
					onSelectImage(result)
				}
			}
			reader.readAsDataURL(file)
		},
		[onSelectImage, onError],
	)

	return (
		<>
			<input
				ref={inputRef}
				type={INPUT_TYPE.FILE}
				accept={CAMPAIGN_IMAGE_ACCEPTED_EXTENSIONS}
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
			{image ? (
				<>
					<Tooltip title="Modifier l'image de la campagne">
						<Box
							className={classes.namingThumb}
							onClick={(e) => setMenuAnchor(e.currentTarget)}
							style={{ backgroundImage: `url("${image}")` }}
							role="button"
							aria-label="Modifier l'image"
						/>
					</Tooltip>
					<Menu
						anchorEl={menuAnchor}
						open={Boolean(menuAnchor)}
						onClose={() => setMenuAnchor(null)}
					>
						<MenuItem
							onClick={() => {
								setMenuAnchor(null)
								openPicker()
							}}
						>
							Remplacer
						</MenuItem>
						<MenuItem
							onClick={() => {
								setMenuAnchor(null)
								onRemoveImage()
							}}
						>
							Supprimer
						</MenuItem>
					</Menu>
				</>
			) : (
				<Tooltip title={CAMPAIGN_IMAGE_REQUIRED_MSG}>
					<IconButton
						color="primary"
						onClick={openPicker}
						className={
							isMissing
								? `${classes.namingAddImageBtn} ${classes.namingAddImageBtnMissing}`
								: classes.namingAddImageBtn
						}
						aria-label="Ajouter une image"
					>
						<ImageOutlinedIcon />
					</IconButton>
				</Tooltip>
			)}
		</>
	)
}

const campaignModalTheme = createMuiTheme({
	palette: {
		primary: { main: '#009973' },
	},
})

export function CampaignModalSkeleton<TRule>({
	open,
	onClose,
	definition,
	campaignMeta,
	includeRestaurantsStep,
	shops,
	initialSelectedShopIds,
	onSelectedShopIdsChange,
	sections,
	allMenus,
	openErrorModal,
	onLaunch,
	loading = false,
	productDataLoading = false,
	isUpdate = false,
	editCampaign,
	availableOrderTypes,
}: CampaignModalSkeletonProps<TRule>) {
	const classes = useCampaignModalSkeletonStyles()
	const [step, setStep] = useState(0)
	const [rule, setRule] = useState<TRule>(definition.defaultRule)
	const [confirmation, setConfirmation] = useState<CampaignConfirmationState>(() =>
		createDefaultConfirmation(availableOrderTypes, definition),
	)
	const [campaignImageConfig, setCampaignImageConfig] =
		useState<CampaignImageConfig | null>(null)
	const [internalShopIds, setInternalShopIds] = useState<string[]>(initialSelectedShopIds)
	const wasOpenRef = useRef(false)

	const totalSteps = includeRestaurantsStep ? 3 : 2
	const useWideLayout = !CAMPAIGN_WITHOUT_PRODUCT_SELECTION.includes(definition.type)

	const stepLabels = useMemo(
		() =>
			includeRestaurantsStep
				? ['Restaurants', 'Règle promo', 'Confirmation']
				: ['Règle promo', 'Confirmation'],
		[includeRestaurantsStep],
	)

	const isRestStep = includeRestaurantsStep && step === 0
	const isRuleStep = includeRestaurantsStep ? step === 1 : step === 0
	const isConfirmStep = includeRestaurantsStep ? step === 2 : step === 1

	const isBngmCampaign =
		definition.type === CampaignType.BUY_N_GET_M_CHEAPEST_AT_DISCOUNT

	const RuleStepComponent = definition.RuleStep

	useEffect(() => {
		if (!open) {
			wasOpenRef.current = false
			return
		}
		if (wasOpenRef.current) return
		wasOpenRef.current = true
		setStep(0)
		const nextRule =
			isUpdate && editCampaign && definition.getInitialRuleFromCampaign
				? definition.getInitialRuleFromCampaign(editCampaign, allMenus)
				: definition.defaultRule
		setRule(nextRule)
		const nextConf =
			isUpdate && editCampaign
				? confirmationFromCampaign(editCampaign, availableOrderTypes, definition)
				: createDefaultConfirmation(availableOrderTypes, definition)
		setConfirmation(nextConf)
		setCampaignImageConfig(null)
		setInternalShopIds(initialSelectedShopIds)
	}, [
		open,
		definition,
		isUpdate,
		editCampaign,
		allMenus,
		availableOrderTypes,
		initialSelectedShopIds,
	])

	useEffect(() => {
		if (!open) {
			return
		}
		onSelectedShopIdsChange(internalShopIds)
	}, [internalShopIds, open, onSelectedShopIdsChange])

	useEffect(() => {
		if (!open || !isBngmCampaign) {
			return
		}

		setRule((currentRule: TRule) => {
			const nextRule = currentRule as TRule & {
				scopeType?: string
				selectedOptionListKey?: string | null
			}
			if (
				nextRule.scopeType !== BNGM_SCOPE.OPTIONS ||
				!nextRule.selectedOptionListKey
			) {
				return currentRule
			}

			return {
				...currentRule,
				selectedOptionListKey: null,
			} as TRule
		})
	}, [
		open,
		isBngmCampaign,
	])

	const validationContext = useMemo(
		() => ({
			shops,
			allMenus,
			selectedShopIds: internalShopIds,
			bngmOptionListEligibility: undefined,
		}),
		[shops, allMenus, internalShopIds],
	)

	const validation = definition.validateRule(rule, validationContext)
	const isNamingValid = hasRequiredCampaignNaming({
		supportsCampaignNaming: definition.supportsCampaignNaming,
		isUpdate,
		existingName: editCampaign?.config?.campaignName,
		existingImage: editCampaign?.config?.imageUrl,
		currentName: confirmation.name,
		currentImage: confirmation.image,
	})
	const canProceed = isRestStep
		? internalShopIds.length > 0
		: isRuleStep
			? validation.isValid && isNamingValid
			: true

	// Naming is only enforced when `hasRequiredCampaignNaming` rejects the current
	// state, so update flows that never had a name/image stay untouched.
	const isNamingBlocking = Boolean(definition.supportsCampaignNaming) && !isNamingValid
	const isNameMissing = isNamingBlocking && !confirmation.name.trim()
	const isImageMissing = isNamingBlocking && !confirmation.image
	const namingBlockers = [
		...(isImageMissing ? [CAMPAIGN_IMAGE_REQUIRED_MSG] : []),
		...(isNameMissing ? [CAMPAIGN_NAME_REQUIRED_MSG] : []),
	]
	const nextBlockedReason = isRestStep
		? internalShopIds.length > 0
			? ''
			: 'Sélectionnez au moins un restaurant.'
		: isRuleStep
			? namingBlockers.join(' ')
			: ''

	const handleNext = useCallback(() => {
		if (!canProceed) {
			return
		}
		if (step < totalSteps - 1) {
			setStep((s) => s + 1)
		} else {
			const launchValidation = definition.validateRule(rule, validationContext)
			if (!launchValidation.isValid) {
				const firstError = Object.values(launchValidation.errors)[0]
				if (firstError) {
					openErrorModal(firstError)
				}
				return
			}
			const formValues = definition.buildFormValues({
				rule,
				confirmation,
				selectedShopIds: internalShopIds,
				campaignMeta,
				isUpdate,
				marketingCampaignKey: editCampaign?.marketingCampaignKey,
			})
			onLaunch(
				definition.supportsCampaignNaming
					? {
						...formValues,
						campaignName: confirmation.name.trim(),
						campaignImage: confirmation.image,
						campaignImageConfig,
					}
					: formValues,
				internalShopIds,
			)
		}
	}, [
		canProceed,
		step,
		totalSteps,
		definition,
		rule,
		confirmation,
		internalShopIds,
		campaignMeta,
		isUpdate,
		editCampaign,
		onLaunch,
		campaignImageConfig,
		validationContext,
		openErrorModal,
	])

	const handleBack = useCallback(() => {
		if (step > 0) {
			setStep((s) => s - 1)
		}
	}, [step])

	const displayStepNumber = step + 1
	const launchLabel = isUpdate ? 'Modifier la campagne' : 'Lancer la campagne'

	return (
		<ThemeProvider theme={campaignModalTheme}>
			<Modal
				open={open}
				onClose={onClose}
				disableAutoFocus
				className={classes.overlay}
				BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.42)' } }}
			>
				<Paper
					elevation={8}
					className={useWideLayout ? classes.dialogPaperWide : classes.dialogPaper}
				>
					<Box className={classes.header}>
						<Typography variant="subtitle1" style={{ fontWeight: 500, letterSpacing: '0.01em' }}>
							{campaignMeta.title}
						</Typography>
						<IconButton
							size="small"
							onClick={onClose}
							style={{ color: 'rgba(255,255,255,0.8)' }}
						>
							<CloseIcon />
						</IconButton>
					</Box>

					<Box className={classes.stepperWrap}>
						<Stepper
							activeStep={step}
							style={{ padding: '16px 16px', background: 'transparent' }}
						>
							{stepLabels.map((label) => (
								<Step key={label}>
									<StepLabel>{label}</StepLabel>
								</Step>
							))}
						</Stepper>
					</Box>

					<Box className={classes.body}>
						{isRestStep && (
							<RestaurantsStep
								shops={shops}
								selectedShopIds={internalShopIds}
								onSelectedShopIdsChange={setInternalShopIds}
							/>
						)}
						{isRuleStep && productDataLoading && (
							<Box className={classes.loadingState}>
								<CircularProgress size={32} color="primary" />
								<Typography variant="body2" style={{ color: '#757575' }}>
									Chargement des produits...
								</Typography>
							</Box>
						)}
						{isRuleStep && !productDataLoading && (
							<Box display="flex" flexDirection="column" gridGap={16}>
								{definition.supportsCampaignNaming && (
									<Paper variant="outlined" className={classes.namingCompactRow}>
										<Typography
											variant="body2"
											style={{ fontWeight: 600, color: '#212121', marginBottom: 6 }}
										>
											Titre et image de la campagne
										</Typography>
										<Box display="flex" alignItems="center" style={{ gap: 12 }}>
											<NamingImageControl
												image={confirmation.image}
												isMissing={isImageMissing}
												onSelectImage={(dataUrl) => {
													setConfirmation((c) => ({ ...c, image: dataUrl }))
												}}
												onRemoveImage={() => {
													setConfirmation((c) => ({ ...c, image: null }))
													setCampaignImageConfig(null)
												}}
												onError={openErrorModal}
												classes={classes}
											/>
											<TextField
												fullWidth
												variant="outlined"
												size="small"
												placeholder="ex. 2 pizzas achetées, la 3e à -50%"
												value={confirmation.name}
												onChange={(e) => {
													const name = e.target.value
													setConfirmation((prev) => ({ ...prev, name }))
												}}
												color="primary"
												error={isNameMissing}
												InputProps={{ style: { borderRadius: 10 } }}
											/>
										</Box>
										{namingBlockers.length > 0 && (
											<Typography
												variant="caption"
												className={classes.namingRequirementHint}
												style={{ color: '#d32f2f' }}
											>
												{namingBlockers.join(' ')}
											</Typography>
										)}
									</Paper>
								)}
								<RuleStepComponent
									rule={rule}
									setRule={setRule}
									description={definition.ruleDescription}
									sections={sections}
									openErrorModal={openErrorModal}
									campaignType={definition.type}
									allMenus={allMenus}
									selectedShopIds={internalShopIds}
									shops={shops}
									bngmOptionListEligibility={
										undefined
									}
								/>
							</Box>
						)}
						{isConfirmStep && (
							<ConfirmationStep
								definition={definition}
								rule={rule}
								confirmation={confirmation}
								setConfirmation={setConfirmation}
								availableOrderTypes={availableOrderTypes}
								selectedShopIds={internalShopIds}
								shops={shops}
								includeRestaurantsStep={includeRestaurantsStep}
								sections={sections}
								allMenus={allMenus}
								campaignImageConfig={campaignImageConfig}
								setCampaignImageConfig={setCampaignImageConfig}
							/>
						)}
					</Box>

					<Box className={classes.footer}>
						<Typography variant="caption" style={{ color: '#9e9e9e' }}>
							Étape {displayStepNumber} / {totalSteps}
						</Typography>
						<Box display="flex" gridGap={CAMPAIGN_ICON_TEXT_GAP_PX}>
							{step > 0 && (
								<Button variant="outlined" color="primary" onClick={handleBack}>
									Retour
								</Button>
							)}
							<Tooltip
								title={nextBlockedReason}
								disableHoverListener={!nextBlockedReason}
								disableFocusListener={!nextBlockedReason}
								disableTouchListener={!nextBlockedReason}
							>
								<span>
									<Button
										variant="contained"
										color="primary"
										disabled={!canProceed || loading}
										onClick={handleNext}
									>
										{step < totalSteps - 1 ? 'Continuer' : launchLabel}
									</Button>
								</span>
							</Tooltip>
						</Box>
					</Box>
				</Paper>
			</Modal>
		</ThemeProvider>
	)
}
